import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { message } from 'antd';
import authService from './authService';
import { API_URL } from '../config';

// Configuration globale pour éviter les messages dupliqués
message.config({
  top: 70,
  duration: 3,
  maxCount: 1, // Un seul message à la fois
});

// Cache pour éviter les messages dupliqués
let lastErrorMessage = '';
let lastErrorTime = 0;
let connectionErrorMessageKey = 'connection-error';

const showErrorMessage = (msg: string, duration: number = 3) => {
  const now = Date.now();
  // N'afficher le même message que si plus de 2 secondes se sont écoulées
  if (msg !== lastErrorMessage || now - lastErrorTime > 2000) {
    message.error({
      content: msg,
      duration: duration,
      key: connectionErrorMessageKey,
    });
    lastErrorMessage = msg;
    lastErrorTime = now;
  }
};

const clearConnectionError = () => {
  message.destroy(connectionErrorMessageKey);
  lastErrorMessage = '';
};

// Configuration de base pour Axios
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000, // Augmenté à 30 secondes
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
    // IMPORTANT: Ne jamais ajouter 'Access-Control-Allow-Origin' côté client.
    // C'est un header de RÉPONSE défini par le serveur. L'envoyer dans la requête
    // provoque un échec du preflight (erreur que vous avez observée).
  },
  withCredentials: true, // Important pour les cookies de session (si le backend en émet)
});

// Intercepteur pour ajouter le token JWT à chaque requête
// Dans votre service API (api.ts)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Intercepteur pour gérer les réponses
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Si la requête réussit, effacer le message d'erreur de connexion
    clearConnectionError();
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url} → ${response.status}`);
    }
    return response;
  },
  (error: AxiosError) => {
    if (process.env.NODE_ENV === 'development') {
      console.error(`❌ ${error.config?.method?.toUpperCase()} ${error.config?.url} → ${error.response?.status ?? error.code}`);
    }

    // Gestion des erreurs réseau
    if (error.code === 'ECONNABORTED') {
      console.error('La requête a expiré (timeout)');
      showErrorMessage('La requête a expiré. Veuillez réessayer.', 5);
    } else if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      console.error('Erreur réseau - Impossible de se connecter au serveur');
      showErrorMessage('Impossible de se connecter au serveur. Vérifiez que l\'API est démarrée.', 0); // 0 = reste affiché
    } else if (!error.response) {
      console.error('Erreur réseau - Pas de réponse du serveur');
      showErrorMessage('Le serveur ne répond pas. Vérifiez votre connexion réseau.', 0); // 0 = reste affiché
    } else if (error.response.status === 401) {
      // En dev avec un token factice, ne pas forcer la déconnexion
      const token = localStorage.getItem('token') || '';
      const isMockToken = token.startsWith('mock-jwt-token');
      if (isMockToken) {
        console.warn('401 détecté avec token factice - on ignore la déconnexion automatique');
      } else {
        console.warn('Non autorisé - Redirection vers la page de connexion');
        authService.logout();
        // window.location.href sera géré par logout()
      }
    } else if (error.response.status === 403) {
      console.error('Accès refusé - Vous n\'avez pas les permissions nécessaires');
      showErrorMessage('Accès refusé. Vous n\'avez pas les permissions nécessaires.');
    } else if (error.response.status === 404) {
      console.error('Ressource non trouvée');
      // Pas de notification pour 404, géré par les composants
    } else if (error.response.status >= 500) {
      console.error('Erreur serveur - Veuillez réessayer plus tard');
      showErrorMessage('Erreur serveur. Veuillez réessayer plus tard.');
    }

    return Promise.reject(error);
  }
);

// Fonction utilitaire pour les appels API
export async function apiRequest<T = any>(
  config: AxiosRequestConfig
): Promise<T> {
  try {
    const response = await api.request<T>(config);
    return response.data;
  } catch (error) {
    // L'erreur est déjà traitée par l'intercepteur
    if (axios.isAxiosError(error)) {
      // Gestion des erreurs spécifiques à Axios
      const message = (error.response?.data as any)?.message || error.message;
      throw new Error(message);
    }
    throw error;
  }
};

export default api;
